import type { ActorLookup } from "@istari-frontend/notifications/model";
import type { V3 } from "@istari/istari-client";
import { useMemo } from "react";

import {
  changeRequestMetadataKey,
  useChangeRequestMetadata,
} from "~/change-request/feature/use-change-request-metadata";
import { useUserDisplayNames } from "~/user/feature/use-user-display-names";

import { toActorLookup } from "./actor-lookup";
import { useReportLookupFailures } from "./use-report-lookup-failures";

export type ChangeRequestDetail = { title: string | null; createdById: string | null };
export type ChangeRequestEnrichment = {
  changeRequestDetails: ChangeRequestDetail[];
  requesterNames: ActorLookup[];
};

export function useChangeRequestEnrichment(
  events: V3.SystemChangeRequestOpenedNotificationEvent[],
): ChangeRequestEnrichment {
  const metadata = useChangeRequestMetadata(events);
  const details = useMemo(
    () =>
      events.map((event) => {
        const result = metadata[changeRequestMetadataKey(event.systemId, event.changeRequestId)];
        return {
          title: result?.isError ? null : (result?.data?.title ?? null),
          createdById: result?.isError ? null : (result?.data?.createdById ?? null),
        };
      }),
    [metadata, events],
  );
  const users = useUserDisplayNames(details.map((detail) => detail.createdById));
  useReportLookupFailures(
    "notifications-change-request-lookup-failed",
    events.flatMap((event) => {
      const result = metadata[changeRequestMetadataKey(event.systemId, event.changeRequestId)];
      return result?.isError
        ? [{ error: result.error, context: { systemId: event.systemId, changeRequestId: event.changeRequestId } }]
        : [];
    }),
  );
  useReportLookupFailures(
    "notifications-change-request-requester-lookup-failed",
    Object.entries(users).flatMap(([userId, result]) =>
      result.status === "error" ? [{ error: result.error, context: { userId } }] : [],
    ),
  );
  const names = useMemo(
    () =>
      events.map((event, index) => {
        const result = metadata[changeRequestMetadataKey(event.systemId, event.changeRequestId)];
        const createdById = details[index].createdById;
        return toActorLookup(createdById ? users[createdById] : undefined, !!result?.isLoading);
      }),
    [events, metadata, details, users],
  );
  return { changeRequestDetails: details, requesterNames: names };
}
